import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  Heading,
  Spacer,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import * as React from 'react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { serverUrl } from '../../config';
import { routes } from '../../data/routes';
import { FormContainer } from '../FormContainer';
import { LinkText } from '../LinkText';
import { TextField } from '../TextField';
import { ConfirmPasswordSchema, PasswordSchema } from '../Validation';
import { useInvitations } from '../hooks/useInvitations';

const initialFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
};

type RegistrationFormState = typeof initialFormValues;

const RegistrationSchema = yup.object().shape({
  firstName: yup.string().required('Please enter a first name').max(80),
  lastName: yup.string().required('Please enter a last name').max(100),
  email: yup.string().email().required('Please enter an email').max(254),
  password: PasswordSchema,
  confirmPassword: ConfirmPasswordSchema('password'),
});

const postRegistration = (body: RegistrationFormState, invitationId: string | undefined) =>
  fetch(`${serverUrl}/api/actions/register`, {
    method: 'post',
    body: JSON.stringify({ user: body, invitationId: invitationId }),
    headers: {
      'Content-Type': 'application/json',
    },
  }).then((res) => res.json());

const registerUser = async (
  onSuccess: () => void,
  setSubmissionError: (error: string) => void,
  invitation: string | undefined,
  body: RegistrationFormState
) => {
  try {
    const res = await postRegistration(body, invitation);
    if (res.status === 'error' && res.message) {
      setSubmissionError(res.message);
    } else {
      onSuccess();
    }
  } catch (error) {
    setSubmissionError('Something went wrong, please try again in a minute.');
  }
};

const useRegistration = () => {
  const { invitationId } = useInvitations();
  const [submissionError, setSubmissionError] = useState<string | undefined>(undefined);

  const toast = useToast();
  const navigate = useNavigate();

  const onSuccess = useCallback(() => {
    toast({
      title: 'Registration successful',
      description: 'Please log in',
      status: 'success',
      position: 'top',
    });
    navigate(routes.logIn);
  }, [toast, navigate]);

  const onSubmit = useCallback(
    async (body: any) => await registerUser(onSuccess, setSubmissionError, invitationId, body),
    [onSuccess, invitationId, setSubmissionError]
  );

  return { onSubmit, submissionError };
};

const RegisterForm = () => {
  const { onSubmit, submissionError } = useRegistration();

  return (
    <Formik
      initialValues={initialFormValues}
      validationSchema={RegistrationSchema}
      onSubmit={onSubmit}
    >
      {({ errors, touched, isSubmitting, isValid }) => (
        <Form>
          <Stack spacing={4}>
            <TextField
              name="firstName"
              label="First Name"
              placeholder="First name"
              touched={touched.firstName}
              error={errors.firstName}
            />
            <TextField
              name="lastName"
              label="Last Name"
              placeholder="Last name"
              touched={touched.lastName}
              error={errors.lastName}
            />
            <TextField name="email" label="Email" touched={touched.email} error={errors.email} />
            <TextField
              name="password"
              label="Password"
              touched={touched.password}
              error={errors.password}
              type="password"
            />
            <TextField
              name="confirmPassword"
              label="Confirm Password"
              placeholder="Confirm password"
              touched={touched.confirmPassword}
              error={errors.confirmPassword}
              type="password"
            />
            <FormControl isInvalid={submissionError !== undefined}>
              <FormErrorMessage>{submissionError}</FormErrorMessage>
            </FormControl>
          </Stack>
          <Button
            mt={4}
            isDisabled={!isValid}
            isLoading={isSubmitting}
            loadingText="Registering"
            type="submit"
          >
            Register
          </Button>
        </Form>
      )}
    </Formik>
  );
};

const Register = () => (
  <FormContainer>
    <Box>
      <Heading as="h2" size="lg">
        Registration
      </Heading>
      <Text pt={2}>
        Already have an account? <LinkText to="/log-in">Click here to log in.</LinkText>
      </Text>
    </Box>
    <Spacer h={8} />
    <RegisterForm />
  </FormContainer>
);

export default Register;
